/**
 * CredentialExpiryBanner — passive amber banner shown at the top of the Credentials page
 * when a CampMinder credential's apiKeyExpiresAt is within 30 days.
 *
 * Brand guide §23. Mockup: designs/ai-dashboard-platform-v3/2026-04-23-28-credentials-page-expiry-banner-v3.html
 *
 * Rules:
 *  - daysUntilExpiry <= 30: shows warning banner (amber, dismissible)
 *  - daysUntilExpiry <= 7: shows non-dismissible warning (icon changes to AlertTriangle)
 *  - daysUntilExpiry <= 0: shows error banner (red) — same non-dismissible escalation
 *  - Dismissal is per-session in Redux state (reappears on reload — not localStorage)
 *  - One banner per expiring credential, stacked
 *  - "Reconnect to renew" CTA opens HarvestModal inline (same harvest flow)
 *
 * Constraints:
 *  - Computed client-side from credential.apiKeyExpiresAt — no backend call
 *  - Reconnect stays in-flow — opens HarvestModal, no navigation away
 */

import React from 'react'
import { AlertTriangle, Clock, RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Credential } from '@/types/credentials'
import { computeDaysUntilExpiry, formatExpiryDate, getExpiryTier } from './expiryUtils'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CredentialExpiryBannerProps {
  /** The credential to evaluate for expiry. */
  credential: Credential
  /**
   * Called when the user clicks "Reconnect to renew" / "Reconnect" CTA.
   * Takes no arguments — the current harvest flow starts a single session
   * at the org level and cannot target a specific credential.
   */
  onReconnect: () => void
  /** Called when the user dismisses the banner. Only callable when daysUntilExpiry > 7. */
  onDismiss: (credentialId: string) => void
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * CredentialExpiryBanner renders a single banner for one expiring credential.
 * Consumers render one per expiring credential, stacked, per §23 rule:
 * "One banner per expiring credential. If multiple credentials are expiring,
 * show one banner per credential, stacked."
 */
export const CredentialExpiryBanner: React.FC<CredentialExpiryBannerProps> = ({
  credential,
  onReconnect,
  onDismiss,
}) => {
  const days = computeDaysUntilExpiry(credential.apiKeyExpiresAt)
  const tier = getExpiryTier(days)

  // This component is only rendered for expiring/expired credentials —
  // parent filters to tier !== 'none', but guard defensively here too.
  if (tier === 'none') return null

  const expiryDateFormatted = formatExpiryDate(credential.apiKeyExpiresAt)
  const isDismissible = tier === 'warning' && days !== null && days > 7
  const isExpired = tier === 'expired'

  const campName = credential.campName ?? credential.nickname
  const keyName = credential.keyName ?? 'Your API key'

  return (
    <div
      className={`flex flex-col items-start gap-3.5 p-4 rounded-xl mb-6 ${
        isExpired
          ? 'bg-destructive-foreground border border-destructive'
          : 'bg-warning-foreground border border-warning'
      }`}
      role="alert"
      data-testid={`expiry-banner-${credential.id}`}
    >
      <div className='flex items-center justify-between w-full'>
        <div className='flex items-center'>
          {/* Icon */}
          <div
            className='rounded-full flex items-center justify-center flex-shrink-0'
            aria-hidden="true"
          >
            {/* Use AlertTriangle when <= 7 days or expired — §23 escalation rule */}
            {isDismissible ? (
              <Clock size={18} className="mr-2 text-warning" aria-hidden="true" />
            ) : isExpired ? (
                <AlertTriangle size={18} className="mr-2 text-destructive" aria-hidden="true" />
            ) : (
                  <AlertTriangle size={18} className="mr-2 text-warning" aria-hidden="true" />
            )}
          </div>
          {/* Title */}
          <p className={`text-sm font-bold mb-1 ${isExpired ? 'text-destructive' : 'text-warning'}`}>
            {isExpired
              ? `Your CampMinder connection expired ${expiryDateFormatted}`
              : `Your CampMinder connection expires ${expiryDateFormatted}`}
          </p>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Dismiss link — only shown when daysUntilExpiry > 7 */}
          {isDismissible && (
            <button
              type="button"
              className="text-sm text-warning/70 underline underline-offset-2 hover:text-warning transition-colors"
              onClick={() => onDismiss(credential.id)}
              aria-label={`Remind me later about ${campName} expiry`}
            >
              Remind me later
            </button>
          )}
          <Button
            size="sm"
            className={
              isExpired
                ? 'bg-destructive hover:opacity-90 text-white font-semibold'
                : 'bg-warning hover:opacity-90 text-white font-semibold'
            }
            onClick={() => onReconnect()}
            aria-label={`Reconnect to renew ${campName} credential`}
          >
            <RefreshCw size={13} className="mr-1.5" aria-hidden="true" />
            Reconnect to renew
          </Button>
        </div>
      </div>
      <p className={`text-xs leading-relaxed ${isExpired ? 'text-destructive' : 'text-warning'}`}>
        {isExpired ? (
          <>
            The API key <strong>{keyName}</strong> linked to{' '}
            <strong>{campName}</strong> has expired. Dashboards using this
            connection have stopped syncing. Reconnect to restore data access.
          </>
        ) : (
          <>
            The API key <strong>{keyName}</strong> linked to{' '}
            <strong>{campName}</strong> will expire in{' '}
            <strong>{days} {days === 1 ? 'day' : 'days'}</strong>. After
            expiry, dashboards using this connection will stop syncing.
            Reconnect now to create a fresh key — it only takes a minute.
          </>
        )}
      </p>
      {/* Dismiss X — only shown when daysUntilExpiry > 7 */}
      {isDismissible && (
        <button
          type="button"
          className="flex-shrink-0 text-warning/60 hover:text-warning transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          aria-label={`Dismiss expiry reminder for ${campName}`}
          onClick={() => onDismiss(credential.id)}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
